import {
  CheckCircledIcon,
  CrossCircledIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { Flex, Spinner, Text } from '@radix-ui/themes';
import { verboseDebug } from 'classes/helpers/debug.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import { StreamingProvider } from 'components/machine/camera-stream-viewer/context';
import { VisionCalibrationBoardSelector } from 'components/machine/dialogs/vision-calibration/board-selector';
import { MachineContext } from 'contexts/machine.context';
import { IVerboseComponent } from 'interfaces/i-components';
import { IMenuAction } from 'interfaces/i-menus';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { numberToImperial } from 'lib_ts/classes/math.utilities';
import { VisionHelper } from 'lib_ts/classes/vision.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { CameraSource, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX, RadixColor } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { VisionCameraBoard } from 'lib_ts/enums/vision.enums';
import {
  IVisionDistanceProgress,
  IVisionDistanceRequest,
} from 'lib_ts/interfaces/machine-msg/vision/i-calibrate-distance';
import { IPosition } from 'lib_ts/interfaces/pitches/i-base';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import { WebSocketService } from 'services/web-socket.service';
import { useAuthStore } from 'stores/use-auth-store';

enum TabKey {
  BoardSelection = 'BoardSelection',
  DataCollection = 'DataCollection',
}

interface IStatus {
  label?: string;
  icon: ReactNode | undefined;
  message: string | undefined;
  content: ReactNode | undefined;
  color: RadixColor;
  actionsDirection?: 'row' | 'column';
}

interface IProps extends IVerboseComponent {
  // tell parent what the detected plate distance was
  onUpdate: (position: IPosition) => void;
  // tell parent to finish this flow
  onComplete: () => void;
  onCancel: () => void;
  onPendingCompleteChange?: (pending: boolean) => void;
  // restore a successful measurement from a prior session
  storedPlateDistanceFt?: number;
}

export type IVisionCalibrationDistanceProps = IProps;

export const VisionCalibrationDistanceHoC = (props: IProps) => {
  const { machine } = useContext(MachineContext);

  return (
    <StreamingProvider
      machineID={machine.machineID}
      hwVersion={machine.hardware_version}
      defaultSource={CameraSource.STEREO_CHECK}
    >
      <VisionCalibrationDistance {...props} />
    </StreamingProvider>
  );
};

const VisionCalibrationDistance = (props: IProps) => {
  const {
    machine,
    setSpecialMode,
    update: updateMachine,
  } = useContext(MachineContext);
  const current = useAuthStore((s) => s.current);

  const [activeTab, setActiveTab] = useState(TabKey.BoardSelection);
  const [board, setBoard] = useState<VisionCameraBoard>();

  const onVision = machine.tracking_device === TrackingDevice.TrajektVision;

  // todo: clarify why we need this in a comment
  useEffect(() => {
    setSpecialMode('stereo-check');
    return () => setSpecialMode(undefined);
  }, []);

  // instruct the machine to start distance calibration
  // e.g. show the corresponding overlay
  useEffect(() => {
    if (!board) {
      return;
    }

    const startData: IVisionDistanceRequest = {
      start: true,
      board_type: board,
    };

    verboseDebug(props, 'Start', startData);

    WebSocketService.send(
      WsMsgType.U2M_VisionDistanceRequest,
      startData,
      'VCD mount'
    );

    return () => {
      const stopData: IVisionDistanceRequest = {
        start: false,
      };

      verboseDebug(props, 'Stop', stopData);

      WebSocketService.send(
        WsMsgType.U2M_VisionDistanceRequest,
        stopData,
        'VCD unmount'
      );
    };
  }, [board]);

  // user clicks calibrate => true; progress received => false
  const [calibrating, setCalibrating] = useState(false);

  const [lastProgress, setLastProgress] = useState<
    IVisionDistanceProgress | undefined
  >(() => {
    if (props.storedPlateDistanceFt == null) {
      return undefined;
    }

    return {
      positionFt: {
        px: 0,
        py: props.storedPlateDistanceFt,
        pz: 0,
      },
    };
  });

  // listen for progress messages (i.e. the one after you click calibrate w/ the distance)
  useEffect(() => {
    const callback = async (event: CustomEvent) => {
      const data: IVisionDistanceProgress = event.detail;
      verboseDebug(props, 'Received progress', data);
      setLastProgress(data);
      setCalibrating(false);

      if (data.error) {
        return;
      }

      if (!data.positionFt?.py) {
        return;
      }

      // the user may not hit "Complete" if using Rapsodo, but we still need to know when this cal occurred
      await updateMachine({
        last_vision_plate_calibration_changed: new Date().toISOString(),
      });
    };

    WebSocketHelper.on(WsMsgType.M2U_VisionDistanceProgress, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.M2U_VisionDistanceProgress, callback);
    };
  }, []);

  useEffect(() => {
    if (calibrating) {
      props.onPendingCompleteChange?.(false);
      return;
    }

    const pending = !!lastProgress?.positionFt?.py && !lastProgress.error;

    if (pending) {
      props.onPendingCompleteChange?.(true);
    }
  }, [lastProgress, calibrating, props.onPendingCompleteChange]);

  // after successful response, report the reading
  useEffect(() => {
    if (!lastProgress?.positionFt) {
      return;
    }

    props.onUpdate(lastProgress.positionFt);
  }, [lastProgress]);

  // note: plate distance + inset = release distance
  const insetFt = useMemo(
    () => MachineHelper.getWheelsInsetFt(machine.hardware_version),
    [machine.hardware_version]
  );

  // plate distance, from front of machine (i.e. plane w/ vision cameras) to plate
  const detectedFt = lastProgress?.positionFt?.py ?? 0;

  // plate distance, i.e. release without the inset
  const existingFt = machine.releaseFt - insetFt;

  useEffect(() => {
    if (!lastProgress) {
      // haven't calibrated yet
      return;
    }

    if (!board) {
      return;
    }

    if (lastProgress.error) {
      return;
    }

    // notify the server of the reading, server will determine whether any notifications to slack are necessary
    VisionCameraCalibrationsService.getInstance().postDistanceDelta({
      board: board,
      machineID: machine.machineID,
      newPlateDistanceFt: detectedFt,
    });
  }, [lastProgress, machine.machineID, machine.releaseFt, board, detectedFt]);

  const isSuccess = useMemo(
    () =>
      !!lastProgress && !lastProgress.error && !!lastProgress.positionFt?.py,
    [lastProgress]
  );

  const status = useMemo((): IStatus => {
    if (calibrating) {
      return {
        color: RADIX.COLOR.NEUTRAL,
        icon: <Spinner />,
        label: 'Calibrating...',
        message: undefined,
        content: undefined,
      };
    }

    if (!lastProgress) {
      return {
        color: RADIX.COLOR.ACCENT,
        icon: <InfoCircledIcon />,
        label: 'Waiting to calibrate',
        message:
          'Please ensure the lane is clear and the checker board is present before calibrating.',
        content: undefined,
      };
    }

    if (lastProgress.error) {
      return {
        color: RADIX.COLOR.DANGER,
        icon: <CrossCircledIcon color={RADIX.COLOR.DANGER} />,
        label: 'Calibration Failed',
        message: lastProgress.error,
        content: undefined,
      };
    }

    if (isSuccess) {
      const message =
        onVision && VisionHelper.ALLOW_VISION_RELEASE_DISTANCE_UPDATE
          ? 'Please click save to update your machine settings.'
          : undefined;

      const measuredDisplay = numberToImperial(detectedFt).display;

      const plateDistanceDisplay = numberToImperial(existingFt).display;

      const content =
        current.role === UserRole.admin ? (
          <Flex direction="column">
            <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
              <Text>Vision Plate Distance: {measuredDisplay}</Text>
              <SuperAdminIcon />
            </Flex>
            <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
              <Text>Plate Distance: {plateDistanceDisplay}</Text>
              <SuperAdminIcon />
            </Flex>
          </Flex>
        ) : undefined;

      return {
        color: RADIX.COLOR.SUCCESS,
        icon: <CheckCircledIcon />,
        label: 'Calibration Successful',
        message,
        content,
        actionsDirection: 'row',
      };
    }

    return {
      color: RADIX.COLOR.DANGER,
      icon: <CrossCircledIcon color={RADIX.COLOR.DANGER} />,
      label: 'Calibration Failed',
      message:
        'Calibration failed. Invalid plate distance reported by machine.',
      content: undefined,
    };
  }, [
    calibrating,
    lastProgress,
    isSuccess,
    onVision,
    existingFt,
    detectedFt,
    current.role,
  ]);

  const runCalibration = useCallback(() => {
    if (!board) {
      return;
    }

    setCalibrating(true);
    setLastProgress(undefined);

    const data: IVisionDistanceRequest = {
      calibrate: true,
      board_type: board,
    };

    verboseDebug(props, 'Calibrate', data);

    WebSocketService.send(
      WsMsgType.U2M_VisionDistanceRequest,
      data,
      'VCD calibrate'
    );
  }, [board, props]);

  const calibrateAction: IMenuAction = useMemo(
    () => ({
      label: 'Calibrate',
      size: '2',
      className: 'width-100px',
      color: RADIX.COLOR.ACCENT,
      disabled: calibrating,
      onClick: runCalibration,
    }),
    [calibrating, runCalibration]
  );

  const actions: IMenuAction[] = useMemo(() => {
    if (!board) {
      return [];
    }

    if (isSuccess) {
      return [
        calibrateAction,
        {
          label: 'Complete Calibration',
          size: '2',
          className: 'width-100px',
          color: RADIX.COLOR.SUCCESS,
          onClick: props.onComplete,
        },
      ];
    }

    return [calibrateAction];
  }, [board, isSuccess, calibrateAction, props.onComplete]);

  return (
    <ErrorBoundary identifier="VisionCalibrationDistance">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="2">
          <Text color={RADIX.COLOR.SECONDARY}>
            <ol>
              <li>
                Place the calibration board in front edge of home plate, with
                the checkered side facing the machine.
              </li>

              <li>
                Using the level on top of the calibration board, ensure the
                board is level. Adjust as needed using black lever on base until
                board is level.
              </li>

              <li>
                On the calibration screen, the checkered board will appear. The
                vision cameras will detect the board's location and display a
                color pattern over the checkered area to confirm detection.
              </li>

              <li>Click Calibrate in the callout below the camera stream.</li>

              <li>
                A message will appear if calibration is successful or not. If
                calibration fails, adjust the checkered board and repeat the
                process above.
              </li>
            </ol>
          </Text>

          <CommonTabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabKey)}
            gap="0"
            tabs={[
              {
                value: TabKey.BoardSelection,
                label: 'Board Selection',
                title: board
                  ? 'Reset progress to change the board type'
                  : undefined,
                content: (
                  <Flex direction="column" gap="4">
                    <Text color={RADIX.COLOR.SECONDARY}>
                      Please select the board type you are going to use for the
                      calibration.
                    </Text>

                    <VisionCalibrationBoardSelector
                      value={board ?? null}
                      excluded={[VisionCameraBoard.Manufacturing]} // * manufacturing board should not be used for distance calibration
                      onChange={(value) => {
                        setBoard(value);
                        setLastProgress(undefined);

                        verboseDebug(props, 'Board selected', value);

                        setTimeout(() => {
                          setActiveTab(TabKey.DataCollection);
                        }, 500);
                      }}
                    />
                  </Flex>
                ),
              },
              {
                value: TabKey.DataCollection,
                label: 'Data Collection',
                disabled: !board,
                content: (
                  <Flex direction="column" gap="4">
                    <MachineCameraStreamViewer
                      machineID={machine.machineID}
                      hideSource
                      hideRefreshRate
                    />

                    {board && (
                      <CommonCallout
                        icon={status.icon}
                        color={status.color}
                        header={status.label}
                        text={status.message}
                        content={status.content}
                        flexAlign="start"
                        actionsDirection={status.actionsDirection}
                        actions={actions}
                      />
                    )}
                  </Flex>
                ),
              },
            ]}
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
